import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { PaymentService } from '@/services/paymentService';
import { usePaymentPolling } from '@/hooks/usePaymentPolling';
import type { RenewableAccount } from '@/lib/renewalRequest';
import { PaymentAccessNeededAlert } from './PaymentAccessNeededAlert';
import { QrCodePanel } from './QrCodePanel';
import { BankIdLaunchPanel } from '@/components/shared/BankIdLaunchPanel';
import { SwitchToSameDeviceButton } from '@/components/shared/SwitchToSameDeviceButton';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useTranslations } from 'use-intl';

interface PaymentAuthFlowProps {
  payableIds: string[];
  onPaymentCompleted: (result: any) => void;
  onError: (error: string | Error | unknown) => void;
  error?: string | null;
  /** Source accounts the bank refused to pay from for lack of access (CONSENT_REQUIRED). */
  accessNeeded?: RenewableAccount[] | null;
}

export const PaymentAuthFlow: React.FC<PaymentAuthFlowProps> = ({
  payableIds,
  onPaymentCompleted,
  onError,
  error,
  accessNeeded
}) => {
  const t = useTranslations('consultantPayments.auth');
  const [paymentData, setPaymentData] = useState<any>(null);
  const [isInitiating, setIsInitiating] = useState(false);
  const [hasAutoInitiated, setHasAutoInitiated] = useState(false);

  const paymentService = new PaymentService();

  // Use polling hook with dynamic interval
  const pollingData = usePaymentPolling(
    paymentData?.signingBasketId || '',
    paymentData?.authorisationId || '',
    paymentData?.xRequestId || '',
    paymentData?.bicFi || '',
    {
      refreshInterval: paymentData?.refreshInterval,
      qrToken: paymentData?.qrToken,
      qrCodeImage: paymentData?.qrCodeImage,
      bankIdUrl: paymentData?.bankIdUrl,
      scaRedirect: paymentData?.scaRedirect
    }
  );

  // Reported once per payment: a parent re-creating onPaymentCompleted must not re-report it.
  const reportedPaymentRef = React.useRef<unknown>(null);
  React.useEffect(() => {
    if (pollingData.isCompleted && paymentData && reportedPaymentRef.current !== paymentData) {
      reportedPaymentRef.current = paymentData;
      onPaymentCompleted(paymentData);
    }
  }, [pollingData.isCompleted, paymentData, onPaymentCompleted]);

  // Handle errors
  React.useEffect(() => {
    if (pollingData.error) {
      onError(pollingData.error);
    }
  }, [pollingData.error, onError]);

  // Auto-initiate with "other device" on mount
  React.useEffect(() => {
    if (!hasAutoInitiated && !paymentData) {
      setHasAutoInitiated(true);
      initiatePayment(false); // false = other device
    }
  }, [hasAutoInitiated, paymentData]);

  const initiatePayment = async (sameDevice: boolean) => {
    setIsInitiating(true);
    try {
      const result = await paymentService.paySelected(payableIds, sameDevice);
      setPaymentData(result);
      console.log('Payment initiation result:', result);
    } catch (error) {
      console.error('Payment initiation error:', error);
      // Pass the full error object to allow proper KYC error handling
      onError(error);
    } finally {
      setIsInitiating(false);
    }
  };

  const switchToSameDevice = async () => {
    setPaymentData(null);
    setIsInitiating(true);
    try {
      const result = await paymentService.paySelected(payableIds, true);
      setPaymentData(result);
      console.log('Switched to same device:', result);
    } catch (error) {
      console.error('Same device switch error:', error);
      // Pass the full error object to allow proper KYC error handling
      onError(error);
    } finally {
      setIsInitiating(false);
    }
  };

  const renderAuthMethod = () => {
    if (!paymentData) return null;

    // Show existing valid payment result
    if (paymentData.isExisting) {
      return (
        <div className="text-center">
          <Badge variant="secondary" className="mb-2">{t('existingBadge')}</Badge>
          <p className="text-sm text-muted-foreground">{t('existingBody')}</p>
        </div>
      );
    }

    // Priority 1: BankID URL (mbid/mbid_same_device flow)
    if (pollingData.bankIdUrl) {
      return (
        <BankIdLaunchPanel
          badge={t(paymentData.authMethod === 'mbid_same_device' ? 'sameDeviceBankId' : 'mobileBankId')}
          buttonLabel={t('launchBankId')}
          hint={t('launchHint')}
          url={pollingData.bankIdUrl}
        />
      );
    }

    // Priority 2: Generated QR Code from token (mbid_animated_qr_token flow)
    if (pollingData.generatedQrImage) {
      return (
        <QrCodePanel
          badge={t('qrToken')}
          src={pollingData.generatedQrImage}
          alt={t('qrAlt')}
          hint={t('scanHint')}
          pollingNote={pollingData.isPolling ? t('checking', { ms: paymentData.refreshInterval }) : undefined}
        />
      );
    }

    // Priority 3: QR Code Image (mbid_animated_qr_image flow)
    if (pollingData.qrCodeImage) {
      return (
        <QrCodePanel
          badge={t('animatedQr')}
          src={pollingData.qrCodeImage}
          alt={t('qrAlt')}
          hint={t('scanHint')}
          pollingNote={pollingData.isPolling ? t('refreshing', { ms: paymentData.refreshInterval }) : undefined}
        />
      );
    }

    // Redirect Flow
    if (pollingData.scaRedirect) {
      return (
        <div className="space-y-4">
          <div className="text-center">
            <Badge variant="outline" className="mb-2">{t('bankAuth')}</Badge>
          </div>
          <Button
            onClick={() => window.open(pollingData.scaRedirect!, '_blank')}
            className="w-full"
            size="lg"
          >
            {t('authenticate')}
          </Button>
          <p className="text-xs text-muted-foreground text-center">{t('bankAuthHint')}</p>
        </div>
      );
    }

    // Loading state
    return (
      <div className="text-center py-8">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">{t('settingUp')}</p>
      </div>
    );
  };

  // What shows before the bank has returned a payment to sign.
  const renderBeforePayment = () => {
    if (isInitiating) {
      return (
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t('starting')}</p>
        </div>
      );
    }
    if (accessNeeded) return <PaymentAccessNeededAlert accounts={accessNeeded} />;
    if (error) return <ErrorAlert message={error} />;
    return null;
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!paymentData && renderBeforePayment()}
        {paymentData ? (
          <>
            {error && <ErrorAlert message={error} />}
            {pollingData.error && <ErrorAlert message={pollingData.error} />}

            {renderAuthMethod()}

            {pollingData.isPolling && (
              <div className="text-center pt-2">
                <Badge variant="secondary">{t('status', { status: pollingData.scaStatus })}</Badge>
              </div>
            )}

            {!paymentData.isExisting && (
              <SwitchToSameDeviceButton
                onSwitch={switchToSameDevice}
                isSwitching={isInitiating}
                label={t('sameDevice')}
                switchingLabel={t('switching')}
              />
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default PaymentAuthFlow;